import { FC } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Table } from '@aws-northstar/ui/components'
import {
	Alert,
	Button,
	StatusIndicator,
	Link,
	SpaceBetween
} from '@cloudscape-design/components'
import type { MongoDBRequest } from '../../types/mongodb'
import RequestError from '../../components/MongoDB/RequestError'
import useMongoDBRequests from '../../hooks/query/useMongoDBRequests'

type MongoDBTableItem = MongoDBRequest & { repositoryName: string }

const MongoDB: FC = () => {
	const navigate = useNavigate()
	const location = useLocation()
	const query = useMongoDBRequests()
	const requests: MongoDBTableItem[] = (query.data ?? []).map((request) => ({
		...request,
		repositoryName: request.applicationAlias
			? `gcp-atlas-workload-mongodb-${request.applicationAlias}`
			: '-'
	}))
	const isLoading = query.isFetching
	const error = query.error
	const onRetry = () => {
		void query.refetch()
	}
	const notice = (location.state as { notice?: string } | null)?.notice

	const columnDefinitions = [
		{
			id: 'repositoryName',
			width: 280,
			header: 'Repository Name',
			cell: (data: MongoDBTableItem) => (
				<Link
					href={`/mongodb/${data.mongoDBID}`}
					onFollow={(event) => {
						event.preventDefault()
						navigate(`/mongodb/${data.mongoDBID}`)
					}}
				>
					{data.repositoryName}
				</Link>
			),
			sortingField: 'repositoryName'
		},
		{
			id: 'applicationName',
			width: 140,
			header: 'Application Name',
			cell: (data: MongoDBTableItem) => data.applicationName || '-',
			sortingField: 'applicationName'
		},
		{
			id: 'environment',
			width: 100,
			header: 'Environment',
			cell: (data: MongoDBTableItem) => data.environment || '-',
			sortingField: 'environment'
		},
		{
			id: 'requestorID',
			width: 160,
			header: 'Requested by',
			cell: (data: MongoDBTableItem) => data.requestorID || '-',
			sortingField: 'requestorID'
		},
		{
			id: 'creationTime',
			width: 160,
			header: 'Creation Time',
			cell: (data: MongoDBTableItem) => {
				if (!data.creationTime) return '-'

				const date = new Date(data.creationTime)

				if (Number.isNaN(date.getTime())) return '-'

				const year = date.getFullYear()
				const month = String(date.getMonth() + 1).padStart(2, '0')
				const day = String(date.getDate()).padStart(2, '0')
				const hours = String(date.getHours()).padStart(2, '0')
				const minutes = String(date.getMinutes()).padStart(2, '0')
				const seconds = String(date.getSeconds()).padStart(2, '0')

				return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`
			},
			sortingField: 'creationTime'
		},
		{
			id: 'status',
			width: 140,
			header: 'Status',
			cell: (data: MongoDBTableItem) => {
				switch (data.status) {
					case 'PENDING_APPROVAL':
						return (
							<StatusIndicator type="in-progress" colorOverride="blue">
								Pending Approval
							</StatusIndicator>
						)

					case 'APPROVED':
						return <StatusIndicator type="success">Approved</StatusIndicator>

					case 'REJECTED':
						return <StatusIndicator type="error">Rejected</StatusIndicator>

					default:
						return '-'
				}
			},
			sortingField: 'status'
		}
	]

	const tableActions = (
		<SpaceBetween direction="horizontal" size="xs">
			<Button onClick={onRetry} disabled={query.isFetching}>
				Refresh
			</Button>
			<Button variant="primary" onClick={() => navigate('/mongodb/new')}>
				Onboard MongoDB
			</Button>
		</SpaceBetween>
	)

	return (
		<SpaceBetween direction="vertical" size="l">
			{notice && <Alert type="info">{notice}</Alert>}
			{Boolean(error) && <RequestError error={error} retry={onRetry} />}
			<Table
				header="MongoDB Requests"
				columnDefinitions={columnDefinitions}
				items={requests}
				actions={tableActions}
				trackBy="mongoDBID"
				disableRowSelect
				sortingDisabled={false}
				defaultPageSize={10}
				loading={isLoading}
			/>
		</SpaceBetween>
	)
}

export default MongoDB
